import { useContext, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate, useParams, matchPath } from "react-router-dom";

import ActionButton from "../formInputs/buttons/ActionButton";
import GroupShareForm from "./GroupShareForm";
import LoadingSvg from "../loadingSvg/LoadingSvg";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/GroupShare.scss";

/**
 * GroupShare Component
 *
 * This component manages the UI for sharing access to a selected Elementary Progress Report (EPR)
 * with colleagues. It allows users to:
 * - Select an EPR profile if more than one is available.
 * - Choose a sharing mode (Whole Class or Homeroom Groups).
 * - Pick a specific homeroom group when in Group mode.
 * - Display and manage shares using the GroupShareForm.
 *
 * Props:
 * @param {Object} state - Global or parent state containing `gradingProfiles`, `selectedEpr`, etc.
 * @param {Function} dispatch - Dispatch function for state updates (e.g. setting selected EPR).
 */

const GroupShare = ({ dispatch, state, basePath }) => {
    const { gradingProfileId: paramGradingProfileId, homeroomGroupId: paramHomeroomGroupId } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    // In Admin view, useParams() might not capture sub-paths matched by wildcard.
    // Fallback to manual extraction from pathname using matchPath for robustness.
    const gradingProfileId = useMemo(() => {
        if (paramGradingProfileId) return paramGradingProfileId;
        const match = matchPath(`${basePath || ""}/summary/share/:id/*`, location.pathname);
        const matchedId = match?.params?.id;
        return matchedId && !isNaN(Number(matchedId)) ? matchedId : null;
    }, [paramGradingProfileId, location.pathname, basePath]);

    const homeroomGroupId = useMemo(() => {
        if (paramHomeroomGroupId) return paramHomeroomGroupId;
        const match = matchPath(`${basePath || ""}/summary/share/:id/groups/:groupId/*`, location.pathname);
        const matchedGroupId = match?.params?.groupId;
        return matchedGroupId && !isNaN(Number(matchedGroupId)) ? matchedGroupId : null;
    }, [paramHomeroomGroupId, location.pathname, basePath]);

    const { loader: profileLoader, gradingProfiles, selectedEpr } = state;
    const { state: globalState } = useContext(GlobalContext);
    const { currentTimePeriodDto, schoolYearDto } = globalState || {};

    const gradingProfilesCurrentSem = useMemo(
        () => (gradingProfiles || []).filter((p) => Number(p.timePeriod) === Number(currentTimePeriodDto?.key)),
        [gradingProfiles, currentTimePeriodDto?.key]
    );
    const groupGroups = useMemo(
        () => selectedEpr?.homeroomGroups?.filter((g) => g.type === "GROUP") || [],
        [selectedEpr]
    );

    const wholeClassGroup = useMemo(
        () => selectedEpr?.homeroomGroups?.find((g) => g.type === "WHOLE_CLASS"),
        [selectedEpr]
    );

    const shouldShowEprSelect = (gradingProfilesCurrentSem?.length || 0) > 1;

    const inGroupsPath = useMemo(() => location.pathname.includes("/groups"), [location.pathname]);
    const pendingGroupRedirect = inGroupsPath && !homeroomGroupId && (groupGroups?.length || 0) > 0;

    let selectedHomeroomGroupId;
    if (inGroupsPath) {
        selectedHomeroomGroupId = homeroomGroupId ? Number(homeroomGroupId) : null;
    } else {
        selectedHomeroomGroupId = wholeClassGroup?.key ?? null;
    }

    const goHomeroom = () => {
        if (!gradingProfileId) return;
        navigate(`${basePath || "/home"}/summary/share/${String(gradingProfileId)}`, { replace: true });
    };

    const goGroups = () => {
        if (!gradingProfileId) return;
        if (groupGroups.length > 0) {
            const firstKey = groupGroups[0].key;
            navigate(`${basePath || "/home"}/summary/share/${String(gradingProfileId)}/groups/${String(firstKey)}`, {
                replace: true
            });
        } else {
            navigate(`${basePath || "/home"}/summary/share/${String(gradingProfileId)}/groups`, { replace: true });
        }
    };

    const handleEprChange = (profile) => {
        if (!profile) return;
        const newId = String(profile.key);
        dispatch({ type: "SET_SELECTED_EPR", payload: profile });

        if (inGroupsPath) {
            navigate(`${basePath || "/home"}/summary/share/${newId}/groups`);
        } else {
            navigate(`${basePath || "/home"}/summary/share/${newId}`);
        }
    };

    // Only when we are in /groups (tab or route), have an EPR, and no group id in the URL yet
    useEffect(() => {
        if (!inGroupsPath) return;
        if (!gradingProfileId) return;
        if (homeroomGroupId) return;
        if (!groupGroups || groupGroups.length === 0) return;

        const firstKey = groupGroups[0].key;
        navigate(`${basePath || "/home"}/summary/share/${String(gradingProfileId)}/groups/${String(firstKey)}`, {
            replace: true
        });
    }, [inGroupsPath, gradingProfileId, homeroomGroupId, groupGroups, navigate, basePath]);

    // Sync selectedEpr from URL / default to first, as before
    useEffect(() => {
        if (!Array.isArray(gradingProfilesCurrentSem) || gradingProfilesCurrentSem.length === 0) return;

        if (gradingProfileId) {
            const match = gradingProfilesCurrentSem.find((p) => String(p.key) === String(gradingProfileId));
            if (match && (!selectedEpr || String(selectedEpr.key) !== String(match.key))) {
                dispatch({ type: "SET_SELECTED_EPR", payload: match });
            }
            return;
        }

        if (location.pathname.includes("/summary/share")) {
            const first = gradingProfilesCurrentSem[0];
            if (first) {
                dispatch({ type: "SET_SELECTED_EPR", payload: first });
                navigate(`${basePath || "/home"}/summary/share/${String(first.key)}`, { replace: true });
            }
        }
    }, [gradingProfileId, gradingProfilesCurrentSem, selectedEpr, dispatch, navigate, basePath, location.pathname]);

    const showForm =
        selectedEpr && selectedEpr.gradingProfileStudents.length > 0 && (!inGroupsPath || selectedHomeroomGroupId);
    const showNoRosteredStudents =
        selectedEpr && (!inGroupsPath || selectedHomeroomGroupId) && selectedEpr.gradingProfileStudents.length === 0;
    if (profileLoader) {
        return <LoadingSvg />;
    }
    return (
        <div className="right-side-wrapper">
            <div className="right-page-title">
                <h4>
                    Report Card Share{" "}
                    {selectedEpr && (
                        <span>
                            &ndash;{" "}
                            {selectedEpr.gradeName === "K"
                                ? "Kindergarten"
                                : `${selectedEpr.gradeName} Grade Homeroom Report Card`}
                        </span>
                    )}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto.name}
                </h5>
            </div>
            <hr />
            {!selectedEpr && (
                <div className="shares-helper-text">
                    Roster management is unavailable until a Homeroom Report Card has been created. If your school has
                    released the grade you teach, please first create a Homeroom Report Card. Otherwise, please wait
                    until it becomes available.
                </div>
            )}
            {selectedEpr && (
                <div className="shares-helper-text">
                    Now that you have rostered your homeroom Report Card, you may wish to share access for student
                    rating and comment entry with your colleagues. Below you will be able to select share options for
                    all students or (as appliable) share options for the homeroom groups you have created.
                </div>
            )}
            {shouldShowEprSelect && (
                <section className="epr-selector">
                    <h5>Select a Grade:</h5>
                    {gradingProfilesCurrentSem?.length > 1 && (
                        <>
                            <label className="visually-hidden" htmlFor="selectGradeShares">
                                Select a Grade
                            </label>
                            <select
                                className="epr-select-dropdown"
                                id="selectGradeShares"
                                value={selectedEpr?.key || ""}
                                onChange={(e) => {
                                    const selected = gradingProfilesCurrentSem.find(
                                        (p) => p.key.toString() === e.target.value.toString()
                                    );
                                    handleEprChange(selected);
                                }}
                            >
                                <option value="" disabled>
                                    Select Grade
                                </option>
                                {gradingProfilesCurrentSem
                                    .sort((a, b) => a.grade - b.grade)
                                    .map((profile) => (
                                        <option key={profile.key} value={profile.key}>
                                            {profile.gradeName === "K" ? "Kindergarten" : `${profile.gradeName} Grade`}
                                        </option>
                                    ))}
                            </select>
                        </>
                    )}
                </section>
            )}

            {selectedEpr && (
                <section className="share-mode">
                    <h5>Choose Sharing Mode:</h5>
                    <div className="share-mode-options">
                        <div className="mode-wrapper">
                            <button className={`button-outline ${inGroupsPath ? "" : "active"}`} onClick={goHomeroom}>
                                <div className="button-content">
                                    <div className="button-label">Whole Class</div>
                                    <div className="button-helper">
                                        (Sharing all students to Art, Music, PE Teachers)
                                    </div>
                                </div>
                            </button>
                        </div>
                        <div className="mode-wrapper">
                            <button className={`button-outline ${inGroupsPath ? "active" : ""}`} onClick={goGroups}>
                                <div className="button-content">
                                    <div className="button-label">Homeroom Groups</div>
                                    <div className="button-helper">
                                        (Sharing specific students for differentiated instruction)
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </section>
            )}
            {inGroupsPath && !groupGroups.length && (
                <div className="no-groups-available-wrapper">
                    <span className="no-groups-text">
                        This Report Card has no homeroom groups available. Please go to the Report Card Roster section
                        to add one or more homeroom groups before sharing.
                    </span>
                    <div className="go-create-btn-wrapper">
                        <ActionButton
                            label="Create and Manage Homeroom Groups"
                            onClick={() =>
                                dispatch({ type: "SET_SELECTED_LINK", payload: "/HOME/SUMMARY/ROSTER/GROUPS" })
                            }
                        />
                    </div>
                </div>
            )}

            {inGroupsPath && groupGroups.length > 0 && !pendingGroupRedirect && (
                <section className="group-tabs">
                    <h5>Choose a Group</h5>

                    <div className="group-tab-list">
                        {groupGroups.map((group) => {
                            const keyStr = String(group.key);

                            const isActive = keyStr === String(homeroomGroupId);
                            return (
                                <button
                                    key={group.key}
                                    className={`button-outline ${isActive ? "active" : ""}`}
                                    onClick={() => {
                                        navigate(
                                            `${basePath || "/home"}/summary/share/${String(gradingProfileId)}/groups/${String(group.key)}`
                                        );
                                    }}
                                >
                                    {group.name}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {showForm && (
                <GroupShareForm
                    dispatch={dispatch}
                    gradingProfileId={selectedEpr.key}
                    homeroomGroupId={selectedHomeroomGroupId}
                    selectedEpr={selectedEpr}
                    state={state}
                />
            )}
            {showNoRosteredStudents && (
                <div className="no-groups-available-wrapper">
                    <span className="no-groups-text">
                        Your homeroom Report Card does not have students rostered. Please go to Manage Roster to import
                        your IC homeroom section before sharing.
                    </span>
                    <div className="go-create-btn-wrapper">
                        <ActionButton
                            label="Report Card Roster"
                            onClick={() => dispatch({ type: "SET_SELECTED_LINK", payload: "/HOME/SUMMARY/ROSTER" })}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

GroupShare.propTypes = {
    state: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    basePath: PropTypes.string
};

export default GroupShare;
